import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, ShoppingCart, Grid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/hooks/useCartSync";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import DOMPurify from 'dompurify';
import { DealBadge } from "@/components/DealBadge";
import { useProductDeal } from "@/hooks/useProductDeal";
import { ProductRating } from "@/components/ProductRating";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

interface Product {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_id: string;
  stock: number;
  active: boolean;
  description: string | null;
  category: string | null;
  brand: string | null;
  average_rating?: number;
  review_count?: number;
  vendor?: {
    full_name: string | null;
    business_name: string | null;
    vendor_status: string | null;
    system_role: string | null;
  };
}

const CategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const { addItem } = useCart();
  const { displayCurrency, convertPrice } = useCurrency();

  // Product Card Component with deal support
  const ProductCardItem = ({ product }: { product: Product }) => {
    const { deal, calculateDiscountedPrice } = useProductDeal(product.id);

    return (
      <Card 
        className={`group cursor-pointer hover:shadow-elegant transition-all duration-300 ${
          viewMode === "grid" ? "hover:-translate-y-1" : ""
        }`}
        onClick={() => handleProductClick(product.id)}
      >
        {viewMode === "grid" ? (
          <>
            <div className="relative aspect-square overflow-hidden rounded-t-lg">
              <img
                src={product.images?.[0] || '/placeholder.svg'}
                alt={product.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {product.stock <= 5 && (
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary">
                    Stock: {product.stock}
                  </Badge>
                </div>
              )}
              <div className="absolute top-3 right-3">
                <DealBadge productId={product.id} />
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-lg mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                {product.title}
              </h3>
              {/* Marque et vendeur */}
              <div className="text-xs text-muted-foreground mb-1">
                {product.brand && <span className="font-medium">{product.brand}</span>}
                {product.brand && product.vendor && ' • '}
                {product.vendor && (
                  <span>by {product.vendor.business_name || product.vendor.full_name}</span>
                )}
              </div>
              <ProductRating 
                rating={product.average_rating || 0} 
                reviewCount={product.review_count || 0}
                size="sm"
              />
              <div 
                className="text-sm text-muted-foreground mb-3 mt-1 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description || '') }}
              />
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  {deal ? (
                    <>
                      <span className="text-xl font-bold text-primary">
                        {formatPriceWithCurrency(
                          calculateDiscountedPrice(product.price_cents),
                          product.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-primary">
                      {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                  disabled={product.stock <= 0}
                >
                  <ShoppingCart className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </>
        ) : (
          <CardContent className="p-4 flex gap-4">
            <div className="relative w-32 h-32 flex-shrink-0">
              <img
                src={product.images?.[0] || '/placeholder.svg'}
                alt={product.title}
                className="w-full h-full object-cover rounded-lg"
              />
              <div className="absolute top-2 right-2">
                <DealBadge productId={product.id} />
              </div>
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                  {product.title}
                </h3>
                {/* Marque et vendeur */}
                <div className="text-xs text-muted-foreground mb-1">
                  {product.brand && <span className="font-medium">{product.brand}</span>}
                  {product.brand && product.vendor && ' • '}
                  {product.vendor && (
                    <span>by {product.vendor.business_name || product.vendor.full_name}</span>
                  )}
                </div>
                <ProductRating 
                  rating={product.average_rating || 0} 
                  reviewCount={product.review_count || 0}
                  size="sm"
                />
                <div 
                  className="text-sm text-muted-foreground mb-2 mt-1 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description || '') }}
                />
                <Badge variant="secondary" className="mb-2">
                  Stock: {product.stock}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  {deal ? (
                    <>
                      <span className="text-xl font-bold text-primary">
                        {formatPriceWithCurrency(
                          calculateDiscountedPrice(product.price_cents),
                          product.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-primary">
                      {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                  disabled={product.stock <= 0}
                >
                  <ShoppingCart className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  useEffect(() => {
    if (category) {
      fetchProducts();
    }
  }, [category, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const decodedCategory = decodeURIComponent(category || '');
      
      // Normalize category slug for comparison (lowercase, no accents)
      const normalizedSlug = decodedCategory.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // First, find the category by slug
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('id, name, slug')
        .eq('active', true);
      
      if (categoryError) throw categoryError;
      
      // Find matching category by slug (normalized comparison)
      const matchingCategory = (categoryData || []).find(cat => {
        const catSlug = cat.slug?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
        const catName = cat.name?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
        return catSlug === normalizedSlug || catName === normalizedSlug;
      });
      
      if (!matchingCategory) {
        console.log('Category not found:', normalizedSlug);
        setProducts([]);
        setLoading(false);
        return;
      }
      
      // Get all active products for this category from approved vendors
      const { data: allProducts, error } = await supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(full_name, business_name, vendor_status, system_role),
          category_ref:categories!category_id(id, slug, name)
        `)
        .eq('active', true);

      if (error) throw error;
      
      // Filter products by category_id OR category name match, and valid vendors
      const filteredProducts = (allProducts || []).filter(product => {
        // Check vendor validity: approved vendor OR admin/super_admin
        const isApprovedVendor = product.vendor?.vendor_status === 'approved';
        const isAdmin = product.vendor?.system_role === 'admin' || product.vendor?.system_role === 'super_admin';
        if (!product.vendor || (!isApprovedVendor && !isAdmin)) return false;
        
        // Match by category_id
        if (product.category_id === matchingCategory.id) return true;
        
        // Fallback: match by category text field
        if (product.category) {
          const productCategory = product.category.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return productCategory === normalizedSlug || productCategory === matchingCategory.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }
        
        return false;
      });
      
      // Apply sorting
      let sortedProducts = [...filteredProducts];
      switch (sortBy) {
        case 'price_low':
          sortedProducts.sort((a, b) => a.price_cents - b.price_cents);
          break;
        case 'price_high':
          sortedProducts.sort((a, b) => b.price_cents - a.price_cents);
          break;
        case 'name':
          sortedProducts.sort((a, b) => a.title.localeCompare(b.title));
          break;
        default:
          sortedProducts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }
      
      // Fetch ratings for each product
      const productsWithRatings = await Promise.all(
        sortedProducts.map(async (product: any) => {
          try {
            const [ratingResult, countResult] = await Promise.all([
              supabase.rpc('get_product_average_rating', { product_uuid: product.id }),
              supabase.rpc('get_product_review_count', { product_uuid: product.id })
            ]);
            
            return {
              ...product,
              average_rating: ratingResult.data,
              review_count: countResult.data
            };
          } catch (error) {
            console.error('Error fetching ratings for product:', product.id, error);
            return {
              ...product,
              average_rating: 0,
              review_count: 0
            };
          }
        })
      );
      
      setProducts(productsWithRatings);

    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      title: product.title,
      price_cents: product.price_cents,
      currency: product.currency,
      images: product.images || [],
      vendor_id: product.vendor_id
    });
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-6 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i}>
                  <div className="aspect-square bg-muted"></div>
                  <CardContent className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded"></div>
                    <div className="h-3 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium">{decodeURIComponent(category || '')}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">{decodeURIComponent(category || '')}</h1>
            <p className="text-muted-foreground">
              {products.length} produit{products.length !== 1 ? 's' : ''} trouvé{products.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Plus récent</SelectItem>
                <SelectItem value="price_low">Prix croissant</SelectItem>
                <SelectItem value="price_high">Prix décroissant</SelectItem>
                <SelectItem value="name">Nom A-Z</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid/List */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">
              Aucun produit trouvé dans cette catégorie.
            </p>
            <Button onClick={() => navigate('/')}>
              Retour à l'accueil
            </Button>
          </div>
        ) : (
          <div className={
            viewMode === "grid" 
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              : "space-y-4"
          }>
            {products.map((product) => (
              <ProductCardItem key={product.id} product={product} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;